import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import api from '../services/api';

const Collections = () => {
  const { slug } = useParams();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (slug) {
          // Fetch specific collection by slug
          const response = await api.publicAPI.getCollection(slug);
          if (response.data.success) {
            setSelectedCollection(response.data.collection);
          } else {
            setError('Collection not found');
          }
        } else {
          // Fetch all collections
          const response = await api.publicAPI.getCollections();
          if (response.data.success) {
            setCollections(response.data.collections);
          }
        }
      } catch (err) {
        console.error('Error fetching collections:', err);
        setError(api.utils.handleError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Collection</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <div className="space-x-4">
            <Link to="/collections" className="btn-primary">
              View All Collections
            </Link>
            <button 
              onClick={() => window.location.reload()}
              className="btn-secondary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If viewing a specific collection
  if (selectedCollection) {
    return (
      <div className="min-h-screen">
        {/* Collection Hero */}
        <section className="relative h-96 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url('${api.utils.getImageUrl(selectedCollection.image, 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&h=600&fit=crop')}')` 
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-5xl font-bold mb-4">{selectedCollection.name}</h1>
            <p className="text-xl mb-8">{selectedCollection.description}</p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <span>{selectedCollection.products_count || selectedCollection.product_count} Products</span>
              <span>•</span>
              <span>Premium Quality</span>
              <span>•</span>
              <span>Latest Collection</span>
            </div>
          </div>
        </section>

        {/* Collection Products */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold">Products in This Collection</h2>
              <Link 
                to={`/products?collection_id=${selectedCollection.id}`}
                className="btn-secondary"
              >
                View All Products
              </Link>
            </div>
            
            {selectedCollection.products && selectedCollection.products.length > 0 ? (
              <div className="product-grid">
                {selectedCollection.products.map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                    className="card group"
                  >
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img
                        src={api.utils.getImageUrl(product.images?.[0], { width: 600, height: 750, fit: 'cover' })}
                        alt={product.name}
                        className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=500&fit=crop';
                        }}
                      />
                      {product.badge && (
                        <span className="absolute top-4 left-4 bg-black text-white text-xs px-2 py-1 rounded">
                          {product.badge}
                        </span>
                      )}
                      {!api.utils.isProductAvailable(product) && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <span className="text-white font-semibold">Out of Stock</span>
                        </div>
                      )}
                    </div>
                    <div className="product-card-info">
                      <h3 className="text-base lg:text-sm font-semibold mb-2 group-hover:text-gray-600 transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg lg:text-base font-bold text-black">
                          ₹{product.price.toLocaleString()}
                        </span>
                        {product.original_price && product.original_price > product.price && (
                          <span className="text-sm text-gray-500 line-through">
                            ₹{product.original_price.toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      {/* Stock and Size Info */}
                      <div className="flex items-center justify-between text-sm">
                        <div>
                          {product.totalStock > 0 ? (
                            <span className={`${product.totalStock <= 10 ? 'text-yellow-600' : 'text-green-600'}`}>
                              {product.totalStock <= 10 ? `Only ${product.totalStock} left` : 'In Stock'}
                            </span>
                          ) : (
                            <span className="text-red-600">Out of Stock</span>
                          )}
                        </div>
                        
                        {/* Available Sizes */}
                        <div className="flex space-x-1">
                          {product.available_sizes?.slice(0, 3).map((size, index) => (
                            <span
                              key={index}
                              className="text-xs bg-gray-100 px-1 py-0.5 rounded"
                            >
                              {size}
                            </span>
                          ))}
                          {product.available_sizes?.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{product.available_sizes.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <h3 className="text-xl font-semibold mb-4">No Products Available</h3>
                <p className="text-gray-600 mb-8">
                  This collection is being updated. Check back soon for amazing products!
                </p>
                <Link to="/products" className="btn-primary">
                  Browse All Products
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Related Collections */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Explore Other Collections</h2>
            <RelatedCollections currentCollectionId={selectedCollection.id} />
          </div>
        </section>
      </div>
    );
  }

  // Collections overview page
  return (
    <div className="min-h-screen">
      {/* Collections Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {collections.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {collections.map((collection) => (
                <Link
                  key={collection.id}
                  to={`/collections/${collection.slug}`}
                  className="group relative overflow-hidden rounded-2xl aspect-[4/5] hover:scale-105 transition-transform duration-300"
                >
                  <img
                    src={api.utils.getImageUrl(collection.image, { width: 1200, height: 1500, fit: 'cover' })}
                    alt={collection.name}
                    className="w-full h-full object-cover object-center"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&h=600&fit=crop';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <h3 className="text-2xl font-bold mb-3">{collection.name}</h3>
                    <p className="text-gray-200 mb-6 line-clamp-2">{collection.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center text-lg font-semibold group-hover:underline">
                        Explore Collection <FiArrowRight className="ml-2" size={20} />
                      </span>
                      <div className="text-right">
                        <p className="text-sm text-gray-300">
                          {collection.product_count} products
                        </p>
                        {collection.sample_products && collection.sample_products.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            From ₹{Math.min(...collection.sample_products.map(p => p.price)).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold mb-4">No Collections Available</h3>
              <p className="text-gray-600 mb-8">Check back soon for new collections!</p>
              <Link to="/products" className="btn-primary">
                Browse All Products
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Featured Collection */}
      <FeaturedCollectionSection />
    </div>
  );
};

// Related Collections Component
const RelatedCollections = ({ currentCollectionId }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedCollections = async () => {
      try {
        const response = await api.publicAPI.getFeaturedCollections();
        if (response.data.success) {
          // Filter out current collection and limit to 3
          const relatedCollections = response.data.collections
            .filter(c => c.id !== currentCollectionId)
            .slice(0, 3);
          setCollections(relatedCollections);
        }
      } catch (err) {
        console.error('Error fetching related collections:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedCollections();
  }, [currentCollectionId]);

  if (loading || collections.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {collections.map((collection) => (
        <Link
          key={collection.id}
          to={`/collections/${collection.slug}`}
          className="group relative overflow-hidden rounded-xl aspect-[4/5] hover:scale-105 transition-transform duration-300"
        >
          <img
            src={api.utils.getImageUrl(collection.image, { width: 1200, height: 1500, fit: 'cover' })}
            alt={collection.name}
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h4 className="text-lg font-bold mb-2">{collection.name}</h4>
            <p className="text-sm text-gray-200 mb-3 line-clamp-2">{collection.description}</p>
            <span className="inline-flex items-center text-sm font-semibold group-hover:underline">
              Explore <FiArrowRight className="ml-1" size={16} />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
};

// Featured Collection Section Component
const FeaturedCollectionSection = () => {
  const [featuredCollection, setFeaturedCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedCollection = async () => {
      try {
        const response = await api.publicAPI.getFeaturedCollections();
        if (response.data.success && response.data.collections.length > 0) {
          // Get the first featured collection
          setFeaturedCollection(response.data.collections[0]);
        }
      } catch (err) {
        console.error('Error fetching featured collection:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedCollection();
  }, []);

  if (loading || !featuredCollection) {
    return null;
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">{featuredCollection.name}</h2>
            <p className="text-xl text-gray-600 mb-8">
              {featuredCollection.description}
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-black rounded-full mr-3"></span>
                Premium quality materials
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-black rounded-full mr-3"></span>
                Contemporary designs
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-black rounded-full mr-3"></span>
                Perfect fit guarantee
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-black rounded-full mr-3"></span>
                Versatile styling options
              </li>
            </ul>
            <div className="flex space-x-4">
              <Link to={`/collections/${featuredCollection.slug}`} className="btn-primary">
                Shop {featuredCollection.name}
              </Link>
              <Link to="/products" className="btn-secondary">
                View All Products
              </Link>
            </div>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl shadow-2xl">
            <img
              src={api.utils.getImageUrl(featuredCollection.image, { width: 1200, height: 1500, fit: 'cover' })}
              alt={featuredCollection.name}
              className="w-full h-full object-cover object-center"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=800&fit=crop';
              }}
            />
            <div className="absolute -bottom-4 -right-4 bg-white p-6 rounded-xl shadow-lg">
              <p className="text-sm font-medium text-gray-600">Products in collection</p>
              <p className="text-2xl font-bold text-black">{featuredCollection.product_count}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Collections; 